import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Subscription = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const token = localStorage.getItem('token');

  const packages = [
    {
      id: 'monthly',
      name: 'Monthly',
      price: 29.99,
      features: ['All subjects', 'Unlimited tests', 'Weekly reports', 'Doubt clearing', 'Cancel anytime'],
    },
    {
      id: 'quarterly',
      name: 'Quarterly',
      price: 79.99,
      features: ['All subjects', 'Unlimited tests', 'Weekly reports', 'Doubt clearing', 'Save 10%'],
      popular: true,
    },
    {
      id: 'yearly',
      name: 'Yearly',
      price: 299.99,
      features: ['All subjects', 'Unlimited tests', 'Weekly reports', 'Doubt clearing', 'Save 17%'],
    },
  ];

  const handleSubscribe = async (packageId) => {
    setLoading(packageId);
    try {
      const response = await axios.post(
        `${API}/payments/checkout?package_id=${packageId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to create checkout session');
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen p-4 md:p-8 lg:p-12">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
            Choose Your Plan
          </h1>
          <p className="text-lg text-muted-foreground">
            Start with 1 month, cancel anytime after 3 tests
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              data-testid={`package-${pkg.id}`}
              className={`card-base p-8 relative ${
                pkg.popular ? 'ring-2 ring-primary' : ''
              }`}
            >
              {pkg.popular && (
                <div className="absolute top-0 right-6 -translate-y-1/2">
                  <span className="bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-bold">
                    Most Popular
                  </span>
                </div>
              )}
              
              <div className="text-center mb-6">
                <h3 className="font-heading text-2xl font-bold mb-2">{pkg.name}</h3>
                <div className="flex items-baseline justify-center">
                  <span className="text-4xl font-bold">${pkg.price}</span>
                  <span className="text-muted-foreground ml-2">/{pkg.id === 'yearly' ? 'year' : (pkg.id === 'quarterly' ? 'quarter' : 'month')}</span>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                {pkg.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center">
                    <Check className="h-5 w-5 text-secondary mr-3 flex-shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                data-testid={`subscribe-${pkg.id}`}
                onClick={() => handleSubscribe(pkg.id)}
                disabled={loading === pkg.id}
                className="w-full rounded-full"
                variant={pkg.popular ? 'default' : 'outline'}
              >
                {loading === pkg.id ? 'Processing...' : 'Subscribe Now'}
              </Button>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Button
            variant="ghost"
            onClick={() => navigate('/parent/dashboard')}
            className="rounded-full"
          >
            Back to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Subscription;